import { Component, Inject, Input, OnDestroy, OnInit } from '@angular/core';
import * as mapboxgl from 'mapbox-gl';
import { Subscription } from 'rxjs';
import { Building } from 'src/app/geometrics-form/buildings/state/building.model';
import { Edge } from 'src/app/geometrics-form/edges/state/edge.model';
import { EdgeQuery } from 'src/app/geometrics-form/edges/state/edge.query';
import { GEOMETRIC_FORM_QUERY, GEOMETRIC_FORM_SERVICE } from 'src/app/geometrics-form/geometric-form.module';
import { GeometricFormQuery } from 'src/app/geometrics-form/state/geometric-form.query';
import { GeometricFormService } from 'src/app/geometrics-form/state/geometric-form.service';
import { GeometricFormType } from 'src/app/geometrics-form/state/geometric-form.type';
import { Vertex, VertexClass } from 'src/app/geometrics-form/vertexes/state/vertex.model';
import { VertexQuery } from 'src/app/geometrics-form/vertexes/state/vertex.query';
import { ProjectUI } from 'src/app/projects/state/project-ui';
import { ProjectQuery } from 'src/app/projects/state/project.query';
import { ProjectService } from 'src/app/projects/state/project.service';
import { MapService } from 'src/app/shared/components/map/map.service';
import { SimulationQuery } from 'src/app/simulation/state/simulation.query';
import { MapDrawService } from '../../shared/components/map/map-draw.service';
import { Plotter, PLOTTER } from '../plotters/plotter';
import { LayerService } from '../service/layer.service';

@Component({
  selector: 'app-layer-list',
  templateUrl: './layer-list.component.html',
  styleUrls: ['./layer-list.component.scss']
})
export class LayerListComponent implements OnInit, OnDestroy {

  @Input()
  bounds: mapboxgl.LngLatBoundsLike;

  isLoading: boolean;
  mapbox: mapboxgl.Map;

  resultMode: boolean;

  private subscriptions: Subscription[];

  constructor(
    @Inject(GEOMETRIC_FORM_SERVICE)
    private geometricServices: GeometricFormService<any>[],
    @Inject(GEOMETRIC_FORM_QUERY)
    private geometricQuerys: GeometricFormQuery<GeometricFormType>[],
    @Inject(PLOTTER)
    private plotters: Plotter<any>[],
    private mapService: MapService,
    private layerService: LayerService,
    private drawService: MapDrawService,
    private simulationQuery: SimulationQuery,
    private projectService: ProjectService,
    private projectQuery: ProjectQuery,
    private edgeQuery: EdgeQuery,
    private vertexQuery: VertexQuery
  ) {
  }

  ngOnInit(): void {
    this.isLoading = true;
    this.subscriptions = [];

    this.mapService.map$.subscribe(m => {
      this.mapbox = m;
      this.loadPlottles();

      if (this.bounds) {
        m.fitBounds(this.bounds, { padding: 40 });
      }
    });

    this.simulationQuery.select(['resultMode'])
      .subscribe(({ resultMode }) => this.resultMode = resultMode);

    const styleSubs = this.mapService.styleChanged$.subscribe(() => {
      this.plotters.forEach(p => p.init(this.mapbox));
      this.loadLayerMap();
    });

    this.subscriptions.push(styleSubs);
  }

  ngOnDestroy(): void {
    this.layerService.disableActions();
    if (this.mapbox) {
      this.plotters.forEach(p => p.clearData(this.mapbox));
      this.drawService.clear();
    }
    this.geometricServices.forEach(service => {
      service.clear();
    });
    this.unsubscribe();
  }

  private loadPlottles() {
    this.loadLayerMap();
    this.isLoading = false;
    this.layerService.enableActions();
  }

  private loadLayerMap() {

    this.geometricServices.forEach(service => {
      this.subscriptions.push(service.get().subscribe());
    });

    this.geometricQuerys.forEach(q => {
      const subsV = q.isVisible$.subscribe(v => {
        this.plotters.find(p => p.type === q.type).visible(this.mapbox, v);
      });

      this.subscriptions.push(subsV);

      const subs = q.selectAll().subscribe(data => {
        this.updateLayer(q.type, data);
      });
      this.subscriptions.push(subs);
    });
  }

  private updateLayer(type: GeometricFormType, data: any[]) {
    this.setData(type, data);

    if (type === 'mitra-building') {
      this.validateBuildings(data);
    } else if (type === 'mitra-vertex') {
      this.validateVertexes(data);
    } else if (type === 'mitra-edge') {
      this.validateEdges(data);
    }
  }

  private setData(type: GeometricFormType, data: any[]) {
    const filtered = data.filter(d => !d.hidder);
    this.plotters.find(p => p.type === type).setElements(this.mapbox, filtered);
  }

  private unsubscribe(): void {
    this.subscriptions.forEach(o => o.unsubscribe());
  }

  private updateProjectUi(ui: Partial<ProjectUI>): void {
    this.projectService.updateUI(this.projectQuery.getActiveId(), ui);
  }

  private validateBuildings(buildings: Building[]) {
    this.updateProjectUi({
      hasBuilding: buildings.length > 0,
      disconnectedBuildings: this.disconnectedBuildingIds(buildings)
    });
  }

  private validateEdges(edges: Edge[]) {
    this.updateProjectUi({
      hasEdges: edges.length > 0
    });

    this.validateCentralOfficeConnection();
  }

  private validateVertexes(vertexes: Vertex[]) {
    const centralOfficeIds: number[] = [];
    const disconnectedIds: number[] = [];

    vertexes.forEach(vertex => {
      if (vertex.vertex_class === VertexClass.CO) {
        centralOfficeIds.push(vertex.id);
      } else if (vertex.edge_count === 0) {
        disconnectedIds.push(vertex.id);
      }
    });

    this.updateProjectUi({
      hasVertexes: vertexes.filter(v => v.vertex_class !== VertexClass.CO).length > 0,
      centralOffices: centralOfficeIds,
      vertexesWithoutEdges: disconnectedIds
    });

    this.validateCentralOfficeConnection();
  }

  private validateCentralOfficeConnection() {
    let centralOfficeConnectedToBox = false;

    this.vertexQuery.findCentralOffices().forEach(vertex => {

      if (!centralOfficeConnectedToBox) {
        const vertixesIds = this.edgeQuery.findConnectedVertixesIds(vertex.id);
        centralOfficeConnectedToBox = this.vertexQuery.some(vertixesIds, VertexClass.UB);
      }
    });
    this.updateProjectUi({ centralOfficeConnectedToBox });
  }

  private disconnectedBuildingIds(buildings: Building[]): number[] {
    return buildings.reduce((result, building) => {
      if (building.vertex === null) {
        result.push(building.id);
      }
      return result;
    }, []);
  }
}
